import * as React from 'react';
import {Mutation} from 'react-apollo';
// @ts-expect-error
import type {DataProxy} from 'react-apollo';

import {Button} from 'shells/button/button';
import {Dialog} from 'shells/dialog/dialog';
import {FlashBanner} from 'shells/flash-banner/flash-banner';

import type {
    DeleteFailedEmails as DeleteFailedEmailsMutation,
    DeleteFailedEmailsVariables as DeleteFailedEmailsMutationVariables,
} from 'nutshell-graphql-types';

import DELETE_FAILED_EMAILS from './graphql/mutations/delete-failed-emails.graphql';

import styles from './email-control-bar.module.css';

const MAX_FAILED_TO_DELETE = 500;

type OwnProps = {
    totalCount: number;
    onAllFailedEmailsDeleted: (proxy: DataProxy) => void;
};

type OnDeleteFailedEmailsProps = {
    onDeleteAllFailedEmails: (arg1: {update: (proxy: DataProxy) => void}) => Promise<any>;
};

type WithDeleteFailedEmails = OwnProps & OnDeleteFailedEmailsProps;

type Props = WithDeleteFailedEmails;

type State = {
    isConfirmingDeleteAll: boolean;
    isDeleting: boolean;
    successfulDeletionCount: number | null | undefined;
    successfulDeletionDeferred: boolean | null | undefined;
    errorMessage: string | null | undefined;
};

class EmailControlBarComponent extends React.PureComponent<Props, State> {
    deleteButton: HTMLElement | null | undefined;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isConfirmingDeleteAll: false,
            isDeleting: false,
            successfulDeletionCount: null,
            successfulDeletionDeferred: null,
            errorMessage: null,
        };
    }

    render() {
        const deleteLabel =
            this.props.totalCount > MAX_FAILED_TO_DELETE
                ? MAX_FAILED_TO_DELETE
                : `all ${this.props.totalCount}`;

        return (
            <div className={styles['email-control-bar']}>
                {this.state.isDeleting ? 'Deleting, please wait…' : undefined}
                {!this.state.isDeleting && this.props.totalCount > 1 ? (
                    <React.Fragment>
                        <Button
                            // @ts-expect-error
                            getButtonRef={(node) => {
                                this.deleteButton = node;
                            }}
                            variant='text-primary'
                            onClick={this.handleDeleteAll}
                        >
                            {`Delete ${deleteLabel} failed emails`}
                        </Button>
                        {this.state.isConfirmingDeleteAll ? (
                            <Dialog
                                question={`Are you sure you want to delete ${deleteLabel} failed emails?`}
                                primaryText='Yes'
                                secondaryText='No'
                                onPrimary={this.handleConfirmDeleteAll}
                                onSecondary={this.handleCancelDeleteAll}
                                isWarning={true}
                                anchor={this.deleteButton}
                            />
                        ) : undefined}
                    </React.Fragment>
                ) : undefined}
                {this.state.successfulDeletionCount ? (
                    <FlashBanner
                        isOverlay={true}
                        duration={4000}
                        type='success'
                        onHidden={() => {
                            this.setState({
                                successfulDeletionCount: null,
                                successfulDeletionDeferred: null,
                            });
                        }}
                    >
                        {this.state.successfulDeletionCount} failed emails{' '}
                        {this.state.successfulDeletionDeferred
                            ? ' are being deleted. This may take a few minutes.'
                            : 'deleted'}
                    </FlashBanner>
                ) : null}
                {this.state.errorMessage ? (
                    <FlashBanner
                        isOverlay={true}
                        duration={4000}
                        type='error'
                        onHidden={() => {
                            this.setState({errorMessage: null});
                        }}
                    >
                        {this.state.errorMessage}
                    </FlashBanner>
                ) : null}
            </div>
        );
    }

    handleCancelDeleteAll = () => {
        this.setState({
            isConfirmingDeleteAll: false,
        });
    };

    handleConfirmDeleteAll = () => {
        this.setState({
            isDeleting: true,
            isConfirmingDeleteAll: false,
        });

        this.props
            .onDeleteAllFailedEmails({
                update: this.props.onAllFailedEmailsDeleted,
            })
            .then((res) => {
                if (res && res.data && res.data.failedEmailsDelete) {
                    this.setState({
                        successfulDeletionCount: res.data.failedEmailsDelete.numEmailsDeleted,
                        successfulDeletionDeferred: res.data.failedEmailsDelete.deleteDeferred,
                    });
                }
                this.setState({isDeleting: false});
            })
            .catch((err) => {
                // @ts-expect-error
                if (typeof window.trackJs !== 'undefined') {
                    // @ts-expect-error
                    window.trackJs.console.error(err);
                    // @ts-expect-error
                    window.trackJs.track('Could not delete failed emails');
                }
                this.setState({
                    errorMessage: 'Could not delete failed emails, please try again.',
                    isDeleting: false,
                });
            });
    };

    handleDeleteAll = () => {
        this.setState({
            isConfirmingDeleteAll: true,
        });
    };
}

class DeleteMutation extends Mutation<
    DeleteFailedEmailsMutation,
    DeleteFailedEmailsMutationVariables
> {}

export const EmailControlBar = (props: OwnProps) => (
    <DeleteMutation
        mutation={DELETE_FAILED_EMAILS}
        variables={{input: {limit: MAX_FAILED_TO_DELETE}}}
        refetchQueries={['GetEmailStats', 'GetFailedMail']}
    >
        {(deleteAllFailedEmails) => (
            <EmailControlBarComponent
                {...props}
                onDeleteAllFailedEmails={({update}) => deleteAllFailedEmails({update})}
            />
        )}
    </DeleteMutation>
);
